import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Layers, HeartHandshake, Sprout } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLab } from "@/context/LabContext";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay: d, ease: "easeOut" },
});

export default function Landing() {
  const { user } = useAuth();
  const { reset } = useLab();
  const navigate = useNavigate();

  const start = () => {
    if (!user) return navigate("/login");
    reset();
    navigate("/lab");
  };

  return (
    <div className="bg-[#FDFBF7]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/17042037/pexels-photo-17042037.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7]/75 via-[#FDFBF7]/88 to-[#FDFBF7]" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 pt-28 pb-24 text-center">
          <motion.p {...fade(0)} className="text-sm uppercase tracking-[0.25em] text-[#6E6860] mb-6">
            A calm space for two
          </motion.p>
          <motion.h1
            {...fade(0.08)}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-[#2D2A26]"
          >
            Between Us
          </motion.h1>
          <motion.p {...fade(0.16)} className="mt-6 text-lg sm:text-xl text-[#4A5D4E] font-medium">
            Turn a disagreement into understanding — together.
          </motion.p>
          <motion.p {...fade(0.24)} className="mt-6 max-w-2xl mx-auto text-base leading-relaxed text-[#6E6860]">
            Most arguments get stuck on opposing positions. Between Us gently guides the two of you
            beneath the surface — to the real feelings and needs underneath — and helps you find caring
            ways forward that honor what you both need. It never takes sides.
          </motion.p>
          <motion.div {...fade(0.32)} className="mt-10">
            <button
              onClick={start}
              data-testid="start-issue-button"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#4A5D4E] text-[#FDFBF7] text-base font-medium hover:bg-[#3B4A3E] transition-all hover:-translate-y-0.5 shadow-md"
            >
              Start a session together
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" strokeWidth={1.75} />
            </button>
            <p className="mt-4 text-sm text-[#A39E94]">Do it together, side by side — or on your own, with AI helping you understand your partner's side.</p>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Layers,
              title: "Name what's really going on",
              body: "Positions are what we say we want. Underneath are the feelings and needs that truly matter to each of you.",
            },
            {
              icon: HeartHandshake,
              title: "Hear each other, without blame",
              body: "We translate frustration into honest needs — no fault-finding, no telling either of you who's right.",
            },
            {
              icon: Sprout,
              title: "Find caring ways forward",
              body: "Get ideas that meet both partners' needs and help you feel closer — not a resentful split-the-difference.",
            },
          ].map((c, i) => (
            <motion.div
              key={c.title}
              {...fade(i * 0.1)}
              className="bg-white border border-[#E8E3D9] rounded-2xl p-7 shadow-sm"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#F5F2EA] mb-4">
                <c.icon className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} />
              </span>
              <h3 className="font-serif text-xl text-[#2D2A26] mb-2">{c.title}</h3>
              <p className="text-[#6E6860] leading-relaxed">{c.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 text-center">
          {!user && (
            <p className="text-[#6E6860]">
              Already have an account?{" "}
              <Link to="/login" className="text-[#4A5D4E] font-medium underline underline-offset-4" data-testid="landing-login-link">
                Sign in
              </Link>
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
